import type { ContractEventMeta } from 'shared/channels/contract';
import {
  DomainMessagesTypes,
  isCreateOrUpdate,
  isDirectoriesBatchCreated,
  isUploadedMessage,
  isUploadOrUpdate,
  type Message,
} from 'shared/domain/messages/message';
import {
  isDocumentationDeleted,
  isDocumentationRestored,
} from 'shared/domain/messages/documentation/isDocumentationDeleted';
import { isDocumentationMoved } from 'shared/domain/messages/documentation/isDocumentationMoved';
import { getEffectiveGalleryDirectory } from '../gallery/model';
import { isPlainObject } from 'shared/utils/object';
import { isCompleteCollectionEvent } from 'shared/channels/entity';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import type { DocumentationDisplayedFile } from '../gallery/types';
import {
  DocumentationChangeKind,
  type DocumentationRefreshTarget,
  type UploadedDocumentationPayload,
} from './types';

/** The message of a documentation change the worker could not save (e.g. overtaken by someone else); none otherwise. */
export function getDocumentationErrorMessageId(event: Message & ContractEventMeta): string | undefined {
  return event.error ? event.messageId : undefined;
}

export function isRemovalChange(kind: DocumentationChangeKind): boolean {
  return kind === DocumentationChangeKind.removed;
}

function isRemoval(event: Message): boolean {
  return isDocumentationDeleted(event) || isDocumentationMoved(event);
}

/** Meaning of a `documentationChannel` message, or `undefined` to ignore it. */
export function classifyDocumentationMessage(
  event: Message
): DocumentationChangeKind | undefined {
  if (!event.data) {
    return undefined;
  }
  if (isRemoval(event)) {
    return DocumentationChangeKind.removed;
  }
  // A finished sync may bring files of others; the read it causes does not sync
  // again at once (the worker throttles refreshes after reads), so it does not loop.
  if (
    isCreateOrUpdate(event) ||
    isDocumentationRestored(event) ||
    isCompleteCollectionEvent(DomainMessagesTypes.allDocumentations, event)
  ) {
    return DocumentationChangeKind.changed;
  }
  return undefined;
}

/** Meaning of a `directoryChannel` message, or `undefined` to ignore it. */
export function classifyDirectoryMessage(
  event: Message
): DocumentationChangeKind | undefined {
  if (!event.data) {
    return undefined;
  }
  if (isRemoval(event)) {
    return DocumentationChangeKind.removed;
  }
  if (
    isUploadOrUpdate(event) ||
    isDirectoriesBatchCreated(event) ||
    isDocumentationRestored(event) ||
    isCompleteCollectionEvent(DomainMessagesTypes.allDirectories, event)
  ) {
    return DocumentationChangeKind.changed;
  }
  return undefined;
}

/** `uploadedEntity` payload as broadcast by the service worker. */
export function isUploadedDocumentationPayload(
  value: unknown
): value is UploadedDocumentationPayload {
  return (
    isPlainObject(value) &&
    typeof value.localId === 'number' &&
    typeof value._id === 'string' &&
    (value.versionId === undefined || typeof value.versionId === 'string')
  );
}

/** Successful upload payload from a `documentationChannel` message. */
export function getUploadedDocumentation(
  event: Message
): UploadedDocumentationPayload | undefined {
  if (!isUploadedMessage(event) || event.error) {
    return undefined;
  }
  // Message data is untyped - accept only the payload shape we rely on.
  return isUploadedDocumentationPayload(event.data) ? event.data : undefined;
}

/**
 * Folder to refresh after a document changed: the folder that owns the open
 * preview, otherwise the current one.
 */
export function resolveDocumentationRefresh(state: {
  galleryOpen: boolean;
  currentDirectory: string;
  gallerySourceDirectoryId: string | null;
  files: DocumentationDisplayedFile[];
  currentSlide: number;
}): DocumentationRefreshTarget {
  if (!state.galleryOpen) {
    return { directoryId: state.currentDirectory, restoreDirectory: false };
  }
  const directoryId = getEffectiveGalleryDirectory(state);
  return {
    directoryId,
    restoreDirectory: !areDirectoryIdsEqual(directoryId, state.currentDirectory),
  };
}
